import React, { useState } from 'react';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { useLanguage } from '../i18n/LanguageContext';
import { useUser } from '../contexts/UserContext';
interface AuthPageProps {
  onNavigate: (page: string) => void;
}
export function AuthPage({
  onNavigate
}: AuthPageProps) {
  const [isLogin, setIsLogin] = useState(true);
  const {
    success
  } = useToast();
  const {
    t
  } = useLanguage();
  const {
    login,
    register
  } = useUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLogin) {
      login(email, password);
      success(t('auth.success.login'));
      if (email.includes('admin')) {
        onNavigate('admin');
      } else if (email.includes('astro')) {
        onNavigate('dashboard-astrologer');
      } else {
        onNavigate('dashboard-user');
      }
    } else {
      register(name, email, password);
      success(t('auth.success.register'));
      onNavigate('dashboard-user');
    }
  };
  return <div className="w-full min-h-[calc(100vh-64px)] bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-id="element-20956">
      <div className="max-w-md w-full space-y-8" data-id="element-20957">
        <div className="text-center" data-id="element-20958">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-20959">
            <span className="text-white font-bold text-2xl" data-id="element-20960">A</span>
          </div>
          <h2 className="text-3xl font-bold text-slate-900" data-id="element-20961">
            {isLogin ? t('auth.login.title') : t('auth.register.title')}
          </h2>
          <p className="mt-2 text-sm text-slate-600" data-id="element-20962">
            {isLogin ? t('auth.login.desc') : t('auth.register.desc')}
          </p>
        </div>

        <Card padding="lg" className="shadow-xl border-none" data-id="element-20963">
          {/* Tabs */}
          <div className="flex p-1 bg-slate-100 rounded-xl mb-8" data-id="element-20964">
            <button onClick={() => setIsLogin(true)} className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-20965">
              {t('auth.tab.login')}
            </button>
            <button onClick={() => setIsLogin(false)} className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${!isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-20966">
              {t('auth.tab.register')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-20967">
            {!isLogin && <Input label={t('auth.name')} placeholder={t('auth.name.placeholder')} icon={<User className="w-4 h-4" data-id="element-20969" />} value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} required data-id="element-20968" />}

            <Input label={t('auth.email')} type="email" placeholder={t('auth.email.placeholder')} icon={<Mail className="w-4 h-4" data-id="element-20971" />} value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} required data-id="element-20970" />

            <div data-id="element-20972">
              <Input label={t('auth.password')} type="password" placeholder={t('auth.password.placeholder')} icon={<Lock className="w-4 h-4" data-id="element-20974" />} value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} required data-id="element-20973" />
              {isLogin && <div className="flex justify-end mt-2" data-id="element-20975">
                  <button type="button" className="text-sm font-medium text-primary hover:text-primary-hover" data-id="element-20976">
                    {t('auth.forgot')}
                  </button>
                </div>}
            </div>

            {!isLogin && <label className="flex items-start gap-2 cursor-pointer group" data-id="element-20977">
                <input type="checkbox" className="mt-1 rounded border-slate-300 text-primary focus:ring-primary" required data-id="element-20978" />
                <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors" data-id="element-20979">
                  {t('auth.agree.text')}{' '}
                  <a href="#" className="text-primary hover:underline" data-id="element-20980">
                    {t('auth.agree.terms')}
                  </a>{' '}
                  {t('auth.agree.and')}{' '}
                  <a href="#" className="text-primary hover:underline" data-id="element-20981">
                    {t('auth.agree.privacy')}
                  </a>
                </span>
              </label>}

            <Button type="submit" size="lg" fullWidth className="mt-6" data-id="element-20982">
              {isLogin ? t('auth.btn.login') : t('auth.btn.register')}
            </Button>
          </form>

          <div className="mt-8" data-id="element-20983">
            <div className="relative" data-id="element-20984">
              <div className="absolute inset-0 flex items-center" data-id="element-20985">
                <div className="w-full border-t border-slate-200" data-id="element-20986" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-20987">
                <span className="px-2 bg-white text-slate-500" data-id="element-20988">
                  {t('auth.social.title')}
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-20989">
              <Button variant="outline" type="button" className="bg-white" data-id="element-20990">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" data-id="element-20991">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-20992" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-20993" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-20994" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-20995" />
                </svg>
                Google
              </Button>
              <Button variant="outline" type="button" className="bg-white text-slate-900 border-slate-200" data-id="element-20996">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" data-id="element-20997">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-20998" />
                </svg>
                Apple
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>;
}